import {
  VStack,
  HStack,
  Stack,
  useColorModeValue,
  Heading,
  FormControl,
  Button,
  Image,
  Textarea,
  Text,
  Spinner,
} from '@chakra-ui/react'
import { useState } from 'react'
import PlotGenSvg from '../assets/plotGen.svg'
import { Configuration, OpenAIApi } from 'openai'

const configuration = new Configuration({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY,
})

const openai = new OpenAIApi(configuration)

const request = {
  model: 'text-davinci-003',
  prompt: '',
  temperature: 0.7,
  max_tokens: 30,
  top_p: 1,
  frequency_penalty: 0,
  presence_penalty: 0,
}

const MoviePlot = () => {
  const [outline, setOutline] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [promptResponse, setPromtResponse] = useState({
    synopsis: '',
    title: '',
    stars: '',
    image_description: '',
    image_url: '',
  })
  async function fetchSynopsis() {
    setIsLoading(true)
    const response = await openai.createCompletion({
      ...request,
      prompt: `
    Here is an example of outline and response.
    Outline: "A big-headed daredevil fighter pilot goes back to school only to be sent on a deadly mission.
    Response: 
    {
    "synopsis": "When bodyguard Kob (Daniel Radcliffe) is recruited by the United Nations to save planet Earth from the sinister Simm (John Malkovich), an alien lord with a plan to take over the world, he reluctantly accepts the challenge. With the help of his loyal sidekick, a brave and resourceful hamster named Gizmo (Gaten Matarazzo), Kob embarks on a perilous mission to destroy Simm. Along the way, he discovers a newfound courage and strength as he battles Simm's merciless forces. With the fate of the world in his hands, Kob must find a way to defeat the alien lord and save the planet.",
    "title": "Zero Earth", 
    "stars": "Daniel Radcliffe, John Malkovich, Gaten Matarazzo",
    "image_description": "A tired and bloodied bodyguard and hamster standing atop a tall skyscraper, looking out over a vibrant cityscape, with a rainbow in the sky above them"
    }

    Your task is to perform the following actions: 
    1 - Generate an engaging, professional and marketable movie synopsis from the outline delimited by triple pound.
    2 - Generate a creative title based on the generated synopsis.
    3 - List out actors suitable to star based on the synopsis.
    4 - Update the synopsis to include actors names in brackets after each character. Choose actors that would be ideal for this role. 
    5 - Short description of an image which could be used to advertise a movie based on a title and synopsis.
    6 - Output only a json object that contains the 
      following keys: synopsis, title, stars, image_description.

    outline: ###${outline}###
    `,
      max_tokens: 360,
    })

    const text = response && response.data.choices[0].text!.trim()
    const responseJson = JSON.parse(extractJSON(text))

    const image_url = await generateImage(responseJson.image_description)
    setPromtResponse({
      ...promptResponse,
      title: responseJson.title,
      stars: responseJson.stars,
      synopsis: responseJson.synopsis,
      image_description: responseJson.image_description,
      image_url: image_url || '',
    })
    setIsLoading(false)
    setOutline('')
  }

  function extractJSON(str: string) {
    let firstOpen, firstClose, candidate
    firstOpen = str.indexOf('{')
    firstClose = str.lastIndexOf('}')
    candidate = str.substring(firstOpen, firstClose + 1)
    return candidate
  }

  async function generateImage(prompt: string) {
    const response = await openai.createImage({
      prompt: prompt,
      n: 1, // 1 to 10
      size: '256x256', // "256x256", 512x512, 1024x1024
      response_format: 'url', // url or b64_json // OpenAI Url only lasts for an hour
    })
    return response.data.data[0].url
  }

  return (
    <VStack
      spacing={{ base: '30px' }}
      minH={{ base: '80vh', md: '80vh' }}
      wrap={'wrap'}
      align={'center'}
      justify={'center'}
      mb={'10px'}
    >
      <HStack
        minH={{ base: '50vh', md: '50vh' }}
        wrap={'wrap'}
        align={'center'}
        justify={'center'}
        spacing={'1rem'}
      >
        <Image src={PlotGenSvg} boxSize={{ base: '220px', md: 'md' }}></Image>
        <Stack
          spacing={4}
          bg={useColorModeValue('white', 'gray.700')}
          rounded={'xl'}
          boxShadow={'lg'}
          p={{ base: '6', md: '12' }}
          mb={{ base: '6', md: '12' }}
          maxW={'lg'}
        >
          <Heading
            lineHeight={1.5}
            fontSize={{ base: '2xl', md: '3xl' }}
            color={'brand.500'}
            alignSelf={'center'}
          >
            PlotGen.AI
          </Heading>
          <Text fontSize={{ base: 'sm', md: 'md' }}>
            Give me a one-sentence concept and I'll give you an eye-catching title, a synopsis the
            studios will love, a movie poster... AND choose the cast for your soon-to-be blockbuster
            movie!
          </Text>

          <FormControl id="email">
            <Textarea
              placeholder="Describe the movie concept in few sentences."
              _placeholder={{ color: 'gray.500' }}
              onChange={(e) => setOutline(e.target.value)}
              value={outline}
              isDisabled={isLoading}
              minW={'20vw'}
            />
          </FormControl>
          <Button
            bg={'brand.500'}
            color={'white'}
            _disabled={{
              bg: 'gray.600',
              cursor: 'not-allowed',
            }}
            _hover={{
              bg: 'brand.600',
            }}
            onClick={(e) => {
              e.preventDefault()
              fetchSynopsis?.()
            }}
            isDisabled={isLoading}
          >
            {isLoading ? 'Generating...' : 'Generate'}
          </Button>
        </Stack>
      </HStack>
      {isLoading && (
        <Spinner thickness="6px" speed="0.25s" emptyColor="gray.200" color="brand.500" size="xl" />
      )}
      {promptResponse.image_url && (
        <HStack
          minH={{ base: '80vh', md: '80vh' }}
          w={'full'}
          wrap={'wrap'}
          align={'center'}
          justify={'center'}
          mb={'10px'}
          spacing={'2rem'}
          bgColor={'brand.50'}
          p={{ base: '4px', md: '12px' }}
        >
          <Stack
            spacing={4}
            bg={'white'}
            rounded={'xl'}
            boxShadow={'lg'}
            p={{ base: '12', md: '20' }}
            my={{ base: '6', md: '12' }}
            alignItems={'center'}
          >
            <Heading
              lineHeight={1.5}
              fontSize={{ base: '2xl', md: '4xl' }}
              color={'brand.500'}
              alignSelf={'center'}
            >
              {promptResponse?.title}
            </Heading>

            <Image src={promptResponse.image_url} boxSize={{ base: 'xs', md: 'xs' }} />

            <Heading
              lineHeight={1}
              fontSize={{ base: 'xl', md: '2xl' }}
              color={'second.500'}
              alignSelf={'center'}
            >
              Cast: {promptResponse?.stars}
            </Heading>
            <Text maxW={'lg'} textAlign={'center'}>
              {promptResponse?.synopsis}
            </Text>
          </Stack>
        </HStack>
      )}
    </VStack>
  )
}
export default MoviePlot
